import type { HttpErrorResponse } from '@angular/common/http';

import type { PickActions } from '@recruitee/ngrx';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  pipeState,
} from '@recruitee/ngrx';
import type { ViewListItem, ViewPosition } from '@recruitee/views-types';

const viewIdSelector = ({ id }: { id: number | string }) => id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<ViewListItem>()(
  'ViewsViewList',
  {
    idSelector: viewIdSelector,
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, viewIdSelector);
      return {
        fetchCollection: (state, payload: { dataSourceId: string }) =>
          defaultActions.fetchCollection(state, { scope: payload.dataSourceId }),

        updateViewsPositions: (
          state,
          payload: { dataSourceId: string; positions: ViewPosition[] },
        ) =>
          pipeState(
            state,
            next => adapter.markAsPending(next, { scope: payload.dataSourceId, entities: [] }),
            next => ({
              ...next,
              scope: {
                ...next.scope,
                [payload.dataSourceId]: [...payload.positions]
                  .sort((a, b) => (a.position > b.position ? 1 : -1))
                  .map(position => position.viewId),
              },
            }),
          ),
        updateViewsPositionsSuccess: (
          state,
          payload: { views: ViewListItem[]; dataSourceId: string },
        ) =>
          defaultActions.fetchCollectionSuccess(state, {
            data: payload.views,
            scope: payload.dataSourceId,
          }),
        updateViewsPositionsFailed: (state, payload: { error: HttpErrorResponse }) =>
          defaultActions.fetchCollectionFailed(state, payload),
        partialUpdate: (state, payload: { id: ViewListItem['id'] } & Partial<ViewListItem>) => {
          const { id, ...changes } = payload;
          const viewListItem = state.entityMap[id];
          if (!viewListItem) return state;

          return adapter.updateOne(state, { id, ...changes });
        },
      };
    },
  },
);
export const ViewListActions: PickActions<
  typeof actions,
  'fetchCollection' | 'updateViewsPositions'
> &
  Pick<typeof actions, 'partialUpdate'> = actions;
export {
  reducer as ViewListReducer,
  namespace as ViewListNamespace,
  selectors as ViewListSelectors,
};
